import {
  emptyDoc, LineMark, Mark, MarkColor, MarkSize, MarkupDoc, newMarkId, rotateDocClockwise, sameDoc, StickerKind,
  StickerMark, STICKERS, StrokeMark, TextMark,
} from './markup';
import {angleFromUp, clampScale, MarkupRenderer, Point, Rect, stickerAngle, stickerPreset, toLocal, toPhoto} from './renderer';

export type MarkupTool = 'move' | 'draw' | 'erase';

/** A grabbable control on the selected mark. */
export type HandleKind = 'rotate' | 'width' | 'end1' | 'end2';

/** `angle`: the mark's turn, so a handle's arrows point along the mark. */
export interface Handle {
  kind: HandleKind;
  screen: Point;
  angle: number;
}

export const MAX_ZOOM = 8;

/** The eraser is a block like a real eraser's end: this tall per wide. */
export const ERASER_ASPECT = 0.62;

const MAX_UNDO = 100;

/** Within 6° of a multiple of 45°, snap to it (level text is easy). */
export const snapAngle = (a: number): number => {
  const nearest = Math.round(a / 45) * 45;
  const snapped = Math.abs(a - nearest) <= 6 ? nearest : a;
  return ((snapped % 360) + 360) % 360;
};

/** Text and the arrow, cut line and X turn; everything else stays upright. */
export const canTurn = (m: Mark): boolean => m.type === 'text' || (m.type === 'sticker' && STICKERS[m.kind].rotatable);

export const colorOf = (m: Mark): MarkColor | null =>
  m.type === 'sticker' ? (STICKERS[m.kind].colorable ? m.color : null) : m.color;

export const sizeOf = (m: Mark): MarkSize => m.size;

const withColor = (m: Mark, c: MarkColor): Mark => ({...m, color: c});

const rectsIntersect = (a: Rect, b: Rect) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * The editor's state and every edit it can make: the web copy of Android's
 * `PhotoMarkupState.kt`, kept out of the component so the gesture code reads
 * as "what happens", not bookkeeping. Screen units are CSS pixels (Android's
 * dp).
 *
 * `doc` is the whole truth: undo is a stack of earlier docs, Reset is a doc
 * with no marks on the original, and Done renders `doc`.
 */
export class PhotoMarkupState {
  readonly initialDoc: MarkupDoc;
  doc: MarkupDoc;
  private undoStack: MarkupDoc[] = [];
  /** What Undo took back, newest last; any new change empties it. */
  private redoStack: MarkupDoc[] = [];

  /**
   * The editor opens with no tool picked (nothing highlighted, "Select a
   * tool" over the buttons); until then the pointer pans and clicks pick
   * marks, as with Move.
   */
  tool: MarkupTool = 'move';
  toolChosen = false;
  showStickers = false;
  selectedId: string | null = null;
  /** The pen: what the next line, text or sticker gets. */
  color: MarkColor = 'red';
  size: MarkSize = 'm';
  eraserSize: MarkSize = 'm';

  /** A freehand line being drawn right now (flat normalized points). */
  liveStroke: number[] = [];

  /** The text box being typed into, right on the photo. */
  editingTextId: string | null = null;

  /** Where the eraser's working end is while it is held (screen), else null. */
  eraserAt: Point | null = null;

  /**
   * How far below the canvas the eraser's tip can never reach (its offset
   * from a finger): the photo may be pushed up this much while erasing.
   */
  eraserReachPx = 0;

  // ---- view: the photo fitted in the canvas, then zoomed and panned ----
  canvasW = 0;
  canvasH = 0;
  photoW = 0;
  photoH = 0;
  zoom = 1;
  pan: Point = {x: 0, y: 0};

  private listeners = new Set<() => void>();
  version = 0;

  constructor(initial: MarkupDoc, readonly originalUrl: string | null) {
    this.initialDoc = initial;
    this.doc = initial;
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getVersion = () => this.version;

  private changed() {
    this.version++;
    this.listeners.forEach(l => l());
  }

  get canUndo() {
    return this.undoStack.length > 0;
  }

  get canRedo() {
    return this.redoStack.length > 0;
  }

  get renderer(): MarkupRenderer | null {
    return this.photoW > 0 && this.photoH > 0 ? new MarkupRenderer(this.photoW, this.photoH) : null;
  }

  setCanvasSize(w: number, h: number) {
    if (w === this.canvasW && h === this.canvasH) return;
    this.canvasW = w;
    this.canvasH = h;
    this.pan = this.clampPan(this.pan);
    this.changed();
  }

  setPhotoSize(w: number, h: number) {
    if (w !== this.photoW || h !== this.photoH) {
      this.photoW = w;
      this.photoH = h;
      this.zoom = 1;
      this.pan = {x: 0, y: 0};
      this.changed();
    }
  }

  fitView() {
    this.zoom = 1;
    this.pan = {x: 0, y: 0};
    this.changed();
  }

  private get fitScale(): number {
    if (!this.photoW || !this.photoH || !this.canvasW || !this.canvasH) return 1;
    return Math.min(this.canvasW / this.photoW, this.canvasH / this.photoH);
  }

  /** Screen pixels per photo pixel. */
  get scale(): number {
    return this.fitScale * this.zoom;
  }

  private get left() {
    return (this.canvasW - this.photoW * this.scale) / 2 + this.pan.x;
  }

  private get top() {
    return (this.canvasH - this.photoH * this.scale) / 2 + this.pan.y;
  }

  toPhoto = (s: Point): Point => ({x: (s.x - this.left) / this.scale, y: (s.y - this.top) / this.scale});
  toScreen = (x: number, y: number): Point => ({x: this.left + x * this.scale, y: this.top + y * this.scale});

  /** Origin for the canvas transform (photo → screen). */
  viewOrigin = (): Point => ({x: this.left, y: this.top});

  /** Pinch/wheel zoom by `factor` about `centroid`, plus a pan. */
  zoomPan(factor: number, panBy: Point, centroid: Point) {
    const anchor = this.toPhoto(centroid);
    this.zoom = Math.min(MAX_ZOOM, Math.max(1, this.zoom * factor));
    const s = this.scale;
    const newLeft = centroid.x - anchor.x * s + panBy.x;
    const newTop = centroid.y - anchor.y * s + panBy.y;
    this.pan = this.clampPan({
      x: newLeft - (this.canvasW - this.photoW * s) / 2,
      y: newTop - (this.canvasH - this.photoH * s) / 2,
    });
    this.changed();
  }

  private clampPan(p: Point): Point {
    const s = this.scale;
    const spareX = Math.max(0, (this.photoW * s - this.canvasW) / 2);
    const spareY = Math.max(0, (this.photoH * s - this.canvasH) / 2);
    const lift = this.tool === 'erase' ? this.eraserReachPx : 0;
    return {x: Math.min(spareX, Math.max(-spareX, p.x)), y: Math.min(spareY, Math.max(-spareY - lift, p.y))};
  }

  get isZoomed() {
    return this.zoom > 1.02;
  }

  // ---- history -------------------------------------------------------------

  private textSessionId: string | null = null;

  private push(before: MarkupDoc) {
    if (this.undoStack.length >= MAX_UNDO) this.undoStack.shift();
    this.undoStack.push(before);
    this.redoStack = [];
  }

  /** One finished change: undoable as a single step. */
  private commit(next: MarkupDoc) {
    if (sameDoc(next, this.doc)) return;
    this.push(this.doc);
    this.doc = next;
    this.changed();
  }

  private dragBefore: MarkupDoc | null = null;

  /** Starts a gesture that changes `doc` continuously (drag, rotate…). */
  beginChange() {
    this.dragBefore = this.doc;
  }

  /** Ends it as ONE undo step, or none if nothing moved. */
  endChange() {
    const before = this.dragBefore;
    if (!before) return;
    this.dragBefore = null;
    if (!sameDoc(before, this.doc)) this.push(before);
    this.changed();
  }

  /** A second finger landed mid-gesture: put things back as they were. */
  cancelChange() {
    if (this.dragBefore) this.doc = this.dragBefore;
    this.dragBefore = null;
    this.changed();
  }

  undo() {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.editingTextId = null;
    this.redoStack.push(this.doc);
    this.doc = prev;
    this.textSessionId = null;
    if (!this.selected) this.selectedId = null;
    this.changed();
  }

  /** Puts back what the last Undo took away (until something new is done). */
  redo() {
    const next = this.redoStack.pop();
    if (!next) return;
    this.editingTextId = null;
    this.undoStack.push(this.doc);
    this.doc = next;
    this.textSessionId = null;
    if (!this.selected) this.selectedId = null;
    this.changed();
  }

  /** Everything off, back to the original photo (one undo step). */
  reset() {
    this.editingTextId = null;
    this.commit(emptyDoc(this.originalUrl));
    this.selectedId = null;
    this.showStickers = false;
    this.changed();
  }

  get canReset() {
    return !sameDoc(this.doc, emptyDoc(this.originalUrl));
  }

  get isDirty() {
    return !sameDoc(this.doc, this.initialDoc);
  }

  // ---- selection and the bottom panel ----------------------------------------

  get selected(): Mark | null {
    const id = this.selectedId;
    return id ? this.doc.marks.find(m => m.id === id) ?? null : null;
  }

  get editingText(): TextMark | null {
    const id = this.editingTextId;
    const m = id ? this.doc.marks.find(x => x.id === id) : undefined;
    return m && m.type === 'text' ? m : null;
  }

  /** The color the color row shows: the selected mark's, else the pen's. */
  get shownColor(): MarkColor {
    const m = this.selected;
    return (m && colorOf(m)) || this.color;
  }

  select(mark: Mark | null) {
    if (mark?.id !== this.editingTextId) this.stopEditingText();
    if (mark?.id !== this.selectedId) this.textSessionId = null;
    this.selectedId = mark?.id ?? null;
    this.changed();
  }

  /** Clicking a text box types into it, right where it sits. */
  editText(mark: TextMark) {
    this.select(mark);
    this.editingTextId = mark.id;
    this.changed();
  }

  /**
   * Typing is over (a click elsewhere, Enter-less Done). A box left empty
   * is taken away again, with the undo step that added it.
   */
  stopEditingText() {
    const m = this.editingText;
    this.editingTextId = null;
    this.textSessionId = null;
    if (m && !m.text.trim()) {
      this.doc = {...this.doc, marks: this.doc.marks.filter(x => x.id !== m.id)};
      while (this.undoStack.length && sameDoc(this.undoStack[this.undoStack.length - 1], this.doc)) this.undoStack.pop();
      if (this.selectedId === m.id) this.selectedId = null;
    }
    this.changed();
  }

  chooseTool(t: MarkupTool) {
    this.stopEditingText();
    this.eraserAt = null;
    this.tool = t;
    this.toolChosen = true;
    this.showStickers = false;
    if (t !== 'move') this.select(null);
    this.changed();
  }

  toggleStickers() {
    this.stopEditingText();
    this.showStickers = !this.showStickers;
    if (this.showStickers) {
      this.tool = 'move';
      this.select(null);
    }
    this.changed();
  }

  /** Recolors the selected mark, or (nothing selected) sets the pen. */
  chooseColor(c: MarkColor) {
    const m = this.selected;
    if (!m) this.color = c;
    else if (colorOf(m) !== null) this.replace(withColor(m, c));
    this.changed();
  }

  chooseSize(s: MarkSize) {
    const m = this.selected;
    if (!m) {
      if (this.tool === 'erase') this.eraserSize = s;
      else this.size = s;
      this.changed();
      return;
    }
    if (m.type === 'sticker') {
      // Same proportion as the step it was at, so a sticker placed while
      // zoomed in keeps fitting what it was placed on.
      this.replace({...m, size: s, scale: clampScale(m.scale * stickerPreset(s, m.kind) / stickerPreset(m.size, m.kind))});
    } else {
      this.replace({...m, size: s});
    }
  }

  /** The size to highlight for the selection. */
  highlightedSize(): MarkSize {
    const m = this.selected;
    if (m) return sizeOf(m);
    return this.tool === 'erase' ? this.eraserSize : this.size;
  }

  deleteSelected() {
    const m = this.selected;
    if (!m) return;
    if (this.editingTextId === m.id) this.editingTextId = null;
    this.commit({...this.doc, marks: this.doc.marks.filter(x => x.id !== m.id)});
    this.select(null);
  }

  setText(text: string) {
    const m = this.editingText ?? (this.selected?.type === 'text' ? this.selected : null);
    if (!m) return;
    if (this.textSessionId !== m.id) {
      // A typing session is one undo step, not one per letter.
      this.push(this.doc);
      this.textSessionId = m.id;
    }
    this.doc = {...this.doc, marks: this.doc.marks.map(x => x.id === m.id ? {...m, text} : x)};
    this.changed();
  }

  rotatePhoto() {
    this.stopEditingText();
    this.commit(rotateDocClockwise(this.doc));
    // The view refits when the new size arrives (setPhotoSize).
  }

  private replace(m: Mark) {
    this.commit({...this.doc, marks: this.doc.marks.map(x => x.id === m.id ? m : x)});
  }

  /** Live replace during a gesture (the gesture's begin/end make the step). */
  private replaceLive(m: Mark) {
    this.doc = {...this.doc, marks: this.doc.marks.map(x => x.id === m.id ? m : x)};
    this.changed();
  }

  // ---- adding marks ------------------------------------------------------------

  /** The middle of what is on screen now, normalized, kept off the edges. */
  private visibleCenter(): [number, number] {
    const c = this.toPhoto({x: this.canvasW / 2, y: this.canvasH / 2});
    const clamp = (v: number) => Math.min(0.9, Math.max(0.1, v));
    return [clamp(c.x / this.photoW), clamp(c.y / this.photoH)];
  }

  private get unit() {
    return Math.min(this.photoW, this.photoH);
  }

  addText() {
    if (!this.photoW) return;
    this.stopEditingText();
    const [x, y] = this.visibleCenter();
    const visibleWidthU = this.canvasW / this.scale / this.unit;
    const z = this.zoom;
    // Letters at their step's size on screen, whatever the zoom: a box added
    // zoomed in is that much smaller on the photo.
    const mark = this.clearOfOtherText({
      type: 'text', id: newMarkId(), color: this.color, size: this.size, x, y,
      width: Math.max(0.15 / z, Math.min(0.7 / z, visibleWidthU * 0.75)), angle: 0, text: '', zoom: z,
    });
    this.commit({...this.doc, marks: [...this.doc.marks, mark]});
    this.afterAdd(mark);
    this.editingTextId = mark.id;
    this.changed();
  }

  /**
   * A new text box lands in the middle of the screen; when a box is already
   * there it goes just under it instead, or just over it with no room below.
   */
  private clearOfOtherText(mark: TextMark): TextMark {
    const r = this.renderer;
    if (!r) return mark;
    const bounds = (m: TextMark): Rect => {
      const b = r.textBox(m);
      const ox = r.px(m.x);
      const oy = r.py(m.y);
      return {left: b.left + ox, top: b.top + oy, right: b.right + ox, bottom: b.bottom + oy};
    };
    const others = this.doc.marks.filter((m): m is TextMark => m.type === 'text').map(bounds);
    const own = bounds(mark);
    const height = own.bottom - own.top;
    const step = (height + r.textSizeOf(mark) * 0.3) / this.photoH;
    const half = height / 2 / this.photoH;
    for (let k = 0; k <= 12; k++) {
      // 0, below, above, two below, two above, …
      const dy = k === 0 ? 0 : step * Math.floor((k + 1) / 2) * (k % 2 === 1 ? 1 : -1);
      const y = mark.y + dy;
      if (y - half < 0 || y + half > 1) continue;
      const moved = {...mark, y};
      const box = bounds(moved);
      if (!others.some(o => rectsIntersect(o, box))) return moved;
    }
    return mark;
  }

  addSticker(kind: StickerKind) {
    if (!this.photoW) return;
    const [x, y] = this.visibleCenter();
    const mark: StickerMark = {
      type: 'sticker', id: newMarkId(), kind, color: this.color, size: this.size,
      // Its step's size on screen, whatever the zoom: zoomed in on a branch,
      // it fits what is on screen, and shrinks with the photo.
      scale: clampScale(stickerPreset(this.size, kind) / this.zoom),
      x, y, angle: 0,
    };
    this.commit({...this.doc, marks: [...this.doc.marks, mark]});
    this.afterAdd(mark);
  }

  private afterAdd(mark: Mark) {
    this.showStickers = false;
    this.tool = 'move';
    this.toolChosen = true;
    this.select(mark);
  }

  // ---- freehand --------------------------------------------------------------

  strokeStart(screen: Point) {
    this.liveStroke = [];
    this.strokeTo(screen);
  }

  strokeTo(screen: Point) {
    const p = this.toPhoto(screen);
    this.liveStroke = [...this.liveStroke, Math.min(1, Math.max(0, p.x / this.photoW)), Math.min(1, Math.max(0, p.y / this.photoH))];
    this.changed();
  }

  strokeEnd() {
    if (this.liveStroke.length >= 2) {
      const mark: StrokeMark = {type: 'stroke', id: newMarkId(), color: this.color, size: this.size, points: this.liveStroke, zoom: this.zoom};
      this.commit({...this.doc, marks: [...this.doc.marks, mark]});
    }
    this.liveStroke = [];
    this.changed();
  }

  strokeCancel() {
    this.liveStroke = [];
    this.changed();
  }

  // ---- eraser: takes the parts of freehand lines it passes over ----------------

  private lastErase: Point | null = null;

  /** Held: its working end shows at `tip`, nothing erased yet. */
  eraseStart(tip: Point) {
    this.beginChange();
    this.lastErase = tip;
    this.eraserAt = tip;
    this.changed();
  }

  /** Follows the pointer without erasing (a finger still settling, or a mouse hovering). */
  eraseHover(tip: Point | null) {
    this.eraserAt = tip;
    this.changed();
  }

  /** Pushed: everything between its last stop and `tip` goes. */
  eraseTo(tip: Point, halfWidthPx: number) {
    this.eraseAlong(this.lastErase ?? tip, tip, halfWidthPx);
    this.lastErase = tip;
  }

  /** One pass is one undo step. */
  eraseEnd(keepShowing = false) {
    this.lastErase = null;
    if (!keepShowing) this.eraserAt = null;
    this.endChange();
  }

  eraseCancel() {
    this.lastErase = null;
    this.eraserAt = null;
    this.cancelChange();
  }

  /**
   * Cuts every freehand line where the eraser's block, swept from `from` to
   * `to`, touches it: the pieces either side stay as lines of their own.
   * Only drawings are erased; text and symbols have Delete. `halfWidthPx` is
   * half the block's width on screen; its height is ERASER_ASPECT of that.
   */
  private eraseAlong(from: Point, to: Point, halfWidthPx: number) {
    this.eraserAt = to;
    const r = this.renderer;
    if (!r) {
      this.changed();
      return;
    }
    const a = this.toPhoto(from);
    const b = this.toPhoto(to);
    const halfW = halfWidthPx / this.scale;
    const halfH = halfW * ERASER_ASPECT;
    // Where the block sat along the way, close enough to leave no gaps.
    const steps = Math.min(500, Math.max(0, Math.floor(Math.hypot(b.x - a.x, b.y - a.y) / halfH)));
    const stops: Point[] = [];
    for (let k = 0; k <= steps; k++) {
      const t = steps === 0 ? 1 : k / steps;
      stops.push({x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t});
    }
    const sweep: Rect = {left: Math.min(a.x, b.x), top: Math.min(a.y, b.y), right: Math.max(a.x, b.x), bottom: Math.max(a.y, b.y)};
    let changed = false;
    const next: Mark[] = [];
    this.doc.marks.forEach(m => {
      if (m.type !== 'stroke') {
        next.push(m);
        return;
      }
      const half = r.strokeWidthOf(m) / 2;
      const reachX = halfW + half;
      const reachY = halfH + half;
      const bb = r.strokeBounds(m);
      const bounds: Rect = {left: bb.left - reachX, top: bb.top - reachY, right: bb.right + reachX, bottom: bb.bottom + reachY};
      if (!(bounds.left <= sweep.right && sweep.left <= bounds.right && bounds.top <= sweep.bottom && sweep.top <= bounds.bottom)) {
        next.push(m);
        return;
      }
      // Fill in long gaps first, so a cut lands where the eraser was.
      const pts = densify(r, m.points, Math.max(1, halfH / 2));
      const pieces: number[][] = [];
      let piece: number[] = [];
      let hit = false;
      for (let i = 0; i + 1 < pts.length; i += 2) {
        const px = r.px(pts[i]);
        const py = r.py(pts[i + 1]);
        if (stops.some(s => Math.abs(px - s.x) <= reachX && Math.abs(py - s.y) <= reachY)) {
          hit = true;
          if (piece.length >= 4) pieces.push(piece);
          piece = [];
        } else {
          piece.push(pts[i], pts[i + 1]);
        }
      }
      if (piece.length >= 4) pieces.push(piece);
      if (!hit) {
        next.push(m);
      } else {
        changed = true;
        pieces.forEach((p, i) => next.push({...m, id: i === 0 ? m.id : newMarkId(), points: p}));
      }
    });
    if (changed) {
      this.doc = {...this.doc, marks: next};
      if (!this.selected) this.selectedId = null;
    }
    this.changed();
  }

  // ---- moving and handles ---------------------------------------------------------

  markAt(screen: Point, touchPx: number): Mark | null {
    const r = this.renderer;
    if (!r) return null;
    const p = this.toPhoto(screen);
    return r.hitTest(this.doc.marks, p.x, p.y, touchPx / this.scale);
  }

  /** Moves a mark by a screen-space drag delta. */
  dragMark(id: string, delta: Point) {
    const m = this.doc.marks.find(x => x.id === id);
    if (!m) return;
    const dx = delta.x / this.scale / this.photoW;
    const dy = delta.y / this.scale / this.photoH;
    const cx = (v: number) => Math.min(1, Math.max(0, v + dx));
    const cy = (v: number) => Math.min(1, Math.max(0, v + dy));
    switch (m.type) {
      case 'stroke': {
        const points = m.points.map((v, i) => i % 2 === 0 ? v + dx : v + dy);
        return this.replaceLive({...m, points});
      }
      case 'text': return this.replaceLive({...m, x: cx(m.x), y: cy(m.y)});
      case 'sticker': return this.replaceLive({...m, x: cx(m.x), y: cy(m.y)});
      case 'line': return this.replaceLive({...m, x1: m.x1 + dx, y1: m.y1 + dy, x2: m.x2 + dx, y2: m.y2 + dy});
    }
  }

  /** Where the selected mark's handles are on screen. */
  handles(stalkPx: number): Handle[] {
    const r = this.renderer;
    const m = this.selected;
    if (!r || !m) return [];
    if (m.type === 'line') {
      return [
        {kind: 'end1', screen: this.toScreen(r.px(m.x1), r.py(m.y1)), angle: 0},
        {kind: 'end2', screen: this.toScreen(r.px(m.x2), r.py(m.y2)), angle: 0},
      ];
    }
    if (m.type === 'stroke') return [];
    const box = r.localBox(m);
    if (!box) return [];
    const [cx, cy, angle] = this.anchorOf(r, m);
    const at = (lx: number, ly: number) => {
      const p = toPhoto(lx, ly, cx, cy, angle);
      return this.toScreen(p.x, p.y);
    };
    // Size is the Size buttons; only what can turn has a turn handle.
    const out: Handle[] = [];
    if (canTurn(m)) out.push({kind: 'rotate', screen: at(0, box.top - stalkPx / this.scale), angle});
    if (m.type === 'text') out.push({kind: 'width', screen: at(box.right, 0), angle});
    return out;
  }

  handleAt(screen: Point, stalkPx: number, touchPx: number): HandleKind | null {
    let best: Handle | null = null;
    let bestD = Infinity;
    this.handles(stalkPx).forEach(h => {
      const d = Math.hypot(h.screen.x - screen.x, h.screen.y - screen.y);
      if (d <= touchPx && d < bestD) {
        best = h;
        bestD = d;
      }
    });
    return best ? (best as Handle).kind : null;
  }

  dragHandle(kind: HandleKind, screen: Point) {
    const r = this.renderer;
    const m = this.selected;
    if (!r || !m) return;
    const p = this.toPhoto(screen);
    const nx = Math.min(1, Math.max(0, p.x / this.photoW));
    const ny = Math.min(1, Math.max(0, p.y / this.photoH));
    if (kind === 'end1' && m.type === 'line') this.replaceLive({...m, x1: nx, y1: ny} as LineMark);
    else if (kind === 'end2' && m.type === 'line') this.replaceLive({...m, x2: nx, y2: ny} as LineMark);
    else if (kind === 'rotate' && (m.type === 'text' || m.type === 'sticker')) {
      const [cx, cy] = this.anchorOf(r, m);
      this.replaceLive({...m, angle: snapAngle(angleFromUp(cx, cy, p.x, p.y))});
    } else if (kind === 'width' && m.type === 'text') {
      const local = toLocal(p.x, p.y, r.px(m.x), r.py(m.y), m.angle);
      const pad = r.textSizeOf(m) * 0.25;
      const w = Math.min(1.5, Math.max(0.12 / Math.max(1, m.zoom), (Math.abs(local.x) - pad) * 2 / r.unit));
      this.replaceLive({...m, width: w});
    }
  }

  private anchorOf(r: MarkupRenderer, m: Mark): [number, number, number] {
    if (m.type === 'text') return [r.px(m.x), r.py(m.y), m.angle];
    if (m.type === 'sticker') return [r.px(m.x), r.py(m.y), stickerAngle(m)];
    return [0, 0, 0];
  }

  /** Repaint without an edit (e.g. hover chrome). */
  touch() {
    this.changed();
  }
}

const densify = (r: MarkupRenderer, points: number[], stepPx: number): number[] => {
  if (points.length < 4) return points;
  const out: number[] = [points[0], points[1]];
  for (let i = 2; i + 1 < points.length; i += 2) {
    const x0 = points[i - 2];
    const y0 = points[i - 1];
    const x1 = points[i];
    const y1 = points[i + 1];
    const len = Math.hypot(r.px(x1) - r.px(x0), r.py(y1) - r.py(y0));
    const n = Math.min(2000, Math.floor(len / stepPx));
    for (let k = 1; k <= n; k++) {
      const t = k / (n + 1);
      out.push(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
    }
    out.push(x1, y1);
  }
  return out;
};
